import classNames from "classnames";
import cuid from "cuid";
import lodash, { filter, find, get, isEmpty, last, set, sortBy } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useEffect, useMemo, useState } from "react";
import {
  useBeforeUnload,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import format from "string-template";
import graphql from "utils/api/graphql";
import { SIGN_DOCUMENT } from "utils/api/graphql/mutations/esign";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import {
  SEND_ADDITIONAL_DOCUMENTS,
  SEND_SUBSCRIPTION_DOCUMENTS,
} from "utils/api/graphql/mutations/subscription";
import { TERMINATE_TASK } from "utils/api/graphql/mutations/task";
import { CREATE_WORKFLOW_WEBHOOK } from "utils/api/graphql/mutations/webhook";
import { LINK } from "utils/api/graphql/queries/link";
import {
  ACTION_TYPES,
  KEYS,
  TASK_STATUSES,
  TEMPLATE_TYPES,
  THEMES_PALETTE,
  TRACER_TYPES,
} from "utils/constants";
import { applyCombinationFromLink } from "utils/helpers/combinations";
import HighContactCard from "./HighContactCard";
import {
  hasLocationStatus,
  isDynamicProcessTheme,
  mergeProcessStatus,
  persistProcessRuntime,
  persistProcessStatus,
  persistStaticResumeStatus,
  readStoredProcessRuntime,
  readStoredStatusId,
  resolveProcess,
  resolveProjectSubscriptionId,
  resolveResumeStatus,
  resolveRuntimeTracer,
  resolveScopedRuntime,
} from "./resolveProcessRuntime";
import Templates from "./Templates";

const lodashOperations = {
  identity: ({ entity, condition }) =>
    lodash[condition.operation](get(entity, condition.leftOperand.accessor)) ===
    condition.rightOperand.value,
  isEmpty: ({ entity, condition }) =>
    lodash[condition.operation](get(entity, condition.leftOperand.accessor)) ===
    condition.rightOperand.value,
  default: ({ entity, condition }) =>
    lodash[condition.operation](
      condition.rightOperand.value,
      get(entity, condition.leftOperand.accessor),
    ),
};

export const filterByConditions = (array, entity) =>
  (array || []).filter((element = {}) => {
    const conditions = element.conditions || [];
    if (!conditions.length) return true;
    return conditions
      .map((condition) => {
        return (
          lodashOperations[condition.operation] || lodashOperations.default
        )({
          entity,
          condition,
        });
      })
      .every((x) => x === true);
  });

const Detail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const [displayProject, setDisplayProject] = useState(null);
  const [author, setAuthor] = useState(get(displayProject, "author"));
  const [addWorkflowWebhook] = useMutation(CREATE_WORKFLOW_WEBHOOK);
  const [signDocuments] = useMutation(SIGN_DOCUMENT);
  const [sendSubscriptionDocuments] = useMutation(SEND_SUBSCRIPTION_DOCUMENTS);
  const [sendAdditionalDocuments] = useMutation(SEND_ADDITIONAL_DOCUMENTS);
  const [addLink] = useMutation(CREATE_LINK);
  const [getLink] = useLazyQuery(LINK, { fetchPolicy: "no-cache" });
  const [terminateTask] = useMutation(TERMINATE_TASK);
  const [assignAttribution] = useMutation(
    graphql.mutations.ASSIGN_PROJECT_ATTRIBUTION,
  );

  const [tracers, setTracers] = useState([]);
  const scopedRuntime = resolveScopedRuntime({
    projectId: id,
    locationState: state,
  });
  const [runtimeProcess, setRuntimeProcess] = useState(scopedRuntime.process);
  const [runtimeTracer, setRuntimeTracer] = useState(scopedRuntime.tracer);
  const [currentStatus, setCurrentStatus] = useState(scopedRuntime.status);
  const [templateHistory, setTemplateHistory] = useState(
    JSON.parse(localStorage.getItem(KEYS.TEMPLATE_HISTORY)),
  );

  useEffect(() => {
    const nextProcess = get(state, "process");
    if (!get(nextProcess, "id")) return;
    setRuntimeProcess(nextProcess);
    setRuntimeTracer(get(state, "tracer"));
    persistProcessRuntime(id, nextProcess, get(state, "tracer"));
  }, [id, state]);

  const handleBeforeUnload = () => {
    localStorage.setItem(
      KEYS.TEMPLATE_HISTORY,
      JSON.stringify(templateHistory),
    );
  };

  useBeforeUnload(handleBeforeUnload, { capture: true });

  useEffect(() => {
    const nextRuntime = resolveScopedRuntime({
      projectId: id,
      locationState: state,
    });
    setDisplayProject(null);
    setAuthor(null);
    setRuntimeProcess(nextRuntime.process);
    setRuntimeTracer(nextRuntime.tracer);
    setCurrentStatus(nextRuntime.status);
  }, [id]);

  const ACTIONS = {
    [ACTION_TYPES.NAVIGATE]: ({ args = {}, actionsTemplate = {} }) => {
      if (isEmpty(actionsTemplate)) return;
      navigate(format(args.url, actionsTemplate));
    },
    [ACTION_TYPES.WEBHOOK]: ({ args = {} }) => {
      if (args.workflow)
        addWorkflowWebhook({
          variables: {
            data: {
              id: cuid(),
              meta: {
                workflow: args.workflow,
                project: id,
              },
            },
          },
        });
    },
    [ACTION_TYPES.SYNC_PROCESSES]: () => {},
    [ACTION_TYPES.NO_SYNC_PROCESSES]: () => {},
    [ACTION_TYPES.SEND_ESIGN_REQUEST]: ({ actionsTemplate = {} }) => {
      if (!get(actionsTemplate, "subscriptionId")) return;
      return signDocuments({
        variables: {
          data: {
            id: get(actionsTemplate, "subscriptionId"),
          },
        },
      });
    },
    [ACTION_TYPES.SEND_SUBSCRIPTION_DOCUMENTS]: ({ actionsTemplate = {} }) => {
      if (!get(actionsTemplate, "subscriptionId")) return;
      return sendSubscriptionDocuments({
        variables: {
          where: {
            id: get(actionsTemplate, "subscriptionId"),
          },
        },
      });
    },
    [ACTION_TYPES.SEND_ADDITIONAL_DOCUMENTS]: ({ actionsTemplate = {} }) => {
      if (!get(actionsTemplate, "subscriptionId")) return;
      return sendAdditionalDocuments({
        variables: {
          where: {
            id: get(actionsTemplate, "subscriptionId"),
          },
        },
      });
    },
    [ACTION_TYPES.ASSIGN_PROJECT]: async () => {
      await assignAttribution({
        variables: { where: { id: displayProject.id } },
      });
    },
    DEFAULT: () => {},
  };

  const [updateProject] = useMutation(graphql.mutations.UPDATE_PROJECT, {
    refetchQueries: [
      {
        query: graphql.queries.PROJECT,
        awaitRefetchQueries: true,
        variables: { where: { id: displayProject?.id || id } },
      },
    ],
  });

  const applyProjectToDisplay = (projectData, currentSubscription) => {
    if (!get(projectData, "id")) return;
    if (currentSubscription) {
      localStorage.setItem(KEYS.SUBSCRIPTION_ID, currentSubscription);
    }
    setDisplayProject({
      ...projectData,
      fields: {
        ...get(projectData, "fields", {}),
        currentSubscription,
      },
    });
    setAuthor(get(projectData, "author"));
    const nextProcess = resolveProcess({
      locationProcess: get(state, "process"),
      storedProcess: get(readStoredProcessRuntime(projectData.id), "process"),
      project: projectData,
    });
    const nextTracer = resolveRuntimeTracer({
      locationTracer: get(state, "tracer"),
      storedTracer: get(readStoredProcessRuntime(projectData.id), "tracer"),
      project: projectData,
    });
    if (get(nextTracer, "id")) {
      setRuntimeTracer(nextTracer);
    }
    if (get(nextProcess, "id")) {
      setRuntimeProcess(nextProcess);
      persistProcessRuntime(projectData.id, nextProcess, nextTracer);
    }
    const processId = get(nextProcess, "id");
    const statuses = sortBy(
      filter(
        projectData.statuses,
        ({ status }) => get(status, "process.id") === processId,
      ),
      "createdDate",
    );
    const linkId = localStorage.getItem(KEYS.LINK_ID);
    if (hasLocationStatus(state) && currentSubscription && linkId) {
      return getLink({
        variables: {
          where: { id: linkId },
        },
        onCompleted: ({ link }) => {
          const belongsToCurrentContext =
            get(link, "project.id") === projectData.id &&
            get(link, "subscription.id") === currentSubscription;
          if (!belongsToCurrentContext) {
            return setCurrentStatus(get(state, "status"));
          }

          const linkCombinationId = get(link, "fields.combinationId");
          if (linkCombinationId) {
            setDisplayProject((prev) =>
              applyCombinationFromLink({
                project: prev || {
                  ...projectData,
                  fields: {
                    ...get(projectData, "fields", {}),
                    currentSubscription,
                  },
                },
                linkCombinationId,
                linkComplementaryId: get(
                  link,
                  "fields.complementaryInsurancePlanId",
                ),
                linkSubscriptionId: get(link, "subscription.id"),
                currentSubscriptionId: currentSubscription,
              }),
            );
          }

          persistProcessStatus(projectData.id, get(link, "fields.status.id"));
          const status = get(nextProcess, "processStatuses", []).find(
            ({ id: statusId }) => statusId === get(link, "fields.status.id"),
          );
          if (status) setCurrentStatus(status);
        },
      });
    }
    const resumeStatus = resolveResumeStatus({
      process: nextProcess,
      projectStatuses: projectData.statuses,
      storedStatusId: readStoredStatusId(projectData.id),
      locationStatus: get(state, "status"),
      preferLocationStatus: get(state, "resumeFromList") === true,
    });
    if (resumeStatus) {
      persistProcessStatus(projectData.id, resumeStatus.id);
      return setCurrentStatus(resumeStatus);
    }
    setCurrentStatus(get(last(statuses), "status"));
  };

  useQuery(graphql.queries.PROJECT, {
    variables: { where: { id } },
    fetchPolicy: "no-cache",
    onCompleted: ({ project }) => {
      if (!project) return;
      localStorage.setItem(KEYS.PROJECT_ID, project?.id);
      if (!isEmpty(displayProject)) return;
      const projectData = omitDeep(project, "__typename");
      const currentSubscription = resolveProjectSubscriptionId({
        locationSubscriptionId: get(state, "subscriptionId"),
        project: projectData,
        storedSubscriptionId: localStorage.getItem(KEYS.SUBSCRIPTION_ID),
      });
      const storedSubscription = get(projectData, "fields.currentSubscription");
      const shouldSyncSubscription =
        currentSubscription && currentSubscription !== storedSubscription;

      if (shouldSyncSubscription) {
        return updateProject({
          variables: {
            where: { id: projectData.id },
            data: {
              fields: {
                ...get(projectData, "fields", {}),
                currentSubscription,
              },
            },
          },
          onCompleted: ({ updateProject: updatedProjectData }) => {
            if (!updatedProjectData) return;
            const updatedProject = omitDeep(updatedProjectData, "__typename");
            applyProjectToDisplay(updatedProject, currentSubscription);
          },
        });
      }

      applyProjectToDisplay(projectData, currentSubscription);
    },
  });

  const onNext = async ({
    payload = {},
    actionsTemplate = {},
    onCompleted = () => {},
    onError = () => {},
  }) => {
    try {
      const project = omitDeep(
        lodash.merge({}, displayProject, payload),
        "__typename",
      );
      const projectStatus = get(displayProject, "statuses", []).find(
        ({ status }) => get(status, "id") === get(currentStatus, "id"),
      );
      const resolvedStatus =
        mergeProcessStatus(currentStatus, get(projectStatus, "status")) ||
        currentStatus;
      const { possibleStatuses, actions } = resolvedStatus || {};
      const [possibleStatus] = filterByConditions(
        sortBy(possibleStatuses, "order"),
        {
          ...project,
          status: resolvedStatus,
        },
      );
      const actionsToExecute = filterByConditions(actions || [], project);
      const syncActions = actionsToExecute
        .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
        .map(({ args: { status } }) => ({
          id: cuid(),
          status: { id: status },
        }));
      const noSyncActions = actionsToExecute
        .filter(({ type }) => type === ACTION_TYPES.NO_SYNC_PROCESSES)
        .map(({ args: { process } }) =>
          get(displayProject, "statuses", []).find(
            ({ status }) => get(status, "process.id") === process,
          ),
        )
        .filter(Boolean);
      const nextStatus = find(
        get(runtimeProcess, "processStatuses"),
        ({ id }) => id === possibleStatus?.nextStatus,
      );

      if (possibleStatus?.nextStatus) {
        setTemplateHistory((prev) => ({
          ...prev,
          [possibleStatus.nextStatus]: currentStatus,
        }));
      }

      let _statuses;
      const currentProcessStatus = get(project, "statuses", []).find(
        ({ status }) => get(status, "process.id") === get(runtimeProcess, "id"),
      );
      const currentOrder = parseInt(
        get(currentProcessStatus, "status.order"),
        10,
      );
      const nextOrder = parseInt(get(nextStatus, "order"), 10);

      if (
        !isNaN(nextOrder) &&
        (isNaN(currentOrder) || nextOrder >= currentOrder)
      ) {
        _statuses = [
          possibleStatus?.nextStatus && {
            id: cuid(),
            status: { id: possibleStatus.nextStatus },
          },
          ...syncActions,
          ...noSyncActions,
        ].filter((entry) => entry && get(entry, "status.id"));
      }

      const sanitizedPayloadStatuses = (payload.statuses || []).filter(
        (entry) => entry && get(entry, "status.id"),
      );
      const validStatuses = _statuses?.length
        ? _statuses
        : sanitizedPayloadStatuses.length
          ? sanitizedPayloadStatuses
          : undefined;

      const dataPayload = {
        ...payload,
        ...(validStatuses ? { statuses: validStatuses } : {}),
      };
      if (payload.statuses && !validStatuses) {
        delete dataPayload.statuses;
      }

      if (isEmpty(dataPayload) && !isEmpty(nextStatus)) {
        persistStaticResumeStatus(get(runtimeProcess, "theme"), nextStatus.id);
        setCurrentStatus(nextStatus);
        onCompleted();
        return;
      }

      return new Promise((resolve, reject) => {
        updateProject({
          variables: {
            where: {
              id: project.id,
            },
            data: dataPayload,
          },
          onCompleted: async ({ updateProject: updatedProjectData }) => {
            try {
              for (const { args, type } of actionsToExecute) {
                await (ACTIONS[type] || ACTIONS.DEFAULT)({
                  args,
                  actionsTemplate,
                });
              }
              const updatedProject = omitDeep(updatedProjectData, "__typename");
              onCompleted();
              setAuthor(get(updatedProject, "author"));
              set(updatedProject, "fields.isManualAssign", undefined);
              set(updatedProject, "author", undefined);
              const currentSubscription =
                get(updatedProject, "fields.currentSubscription") ||
                resolveProjectSubscriptionId({
                  locationSubscriptionId: get(
                    displayProject,
                    "fields.currentSubscription",
                  ),
                  project: updatedProject,
                  storedSubscriptionId: localStorage.getItem(
                    KEYS.SUBSCRIPTION_ID,
                  ),
                });
              if (currentSubscription) {
                localStorage.setItem(KEYS.SUBSCRIPTION_ID, currentSubscription);
              }
              setDisplayProject({
                ...updatedProject,
                fields: {
                  ...get(updatedProject, "fields", {}),
                  ...(currentSubscription ? { currentSubscription } : {}),
                },
              });
              if (get(project, "campaign.id")) {
                terminateTask({
                  variables: {
                    where: {
                      project: { id: project.id },
                      status: TASK_STATUSES.IN_PROGRESS,
                    },
                  },
                });
              }
              if (!isEmpty(nextStatus)) {
                persistStaticResumeStatus(
                  get(runtimeProcess, "theme"),
                  nextStatus.id,
                );
                setCurrentStatus(nextStatus);
              }
              resolve();
            } catch (error) {
              onError(error);
              reject(error);
            }
          },
          onError: (error) => {
            onError(error);
            reject(error);
          },
        });
      });
    } catch (error) {
      onError(error);
      throw error;
    }
  };

  const updateUrl = async ({ subscriptionId, url }) => {
    const { possibleStatuses } = currentStatus;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...displayProject,
        status: currentStatus,
      },
    );
    const nextStatus = find(
      get(runtimeProcess, "processStatuses"),
      ({ id }) => id === possibleStatus?.nextStatus,
    );
    if (isEmpty(nextStatus)) return;
    addLink({
      variables: {
        data: {
          url,
          project: { id },
          subscription: { id: subscriptionId },
          fields: {
            process: { id: get(runtimeProcess, "id") },
            status: { id: nextStatus?.id },
          },
        },
      },
    });
  };

  const onBack = (props) => {
    const steps = props?.steps || 1;
    if (steps === 1 && get(templateHistory, currentStatus?.id)) {
      const previousStatus = get(templateHistory, currentStatus?.id);
      persistStaticResumeStatus(
        get(runtimeProcess, "theme"),
        get(previousStatus, "id"),
      );
      return setCurrentStatus(previousStatus);
    }
    const process = runtimeProcess;
    const sortedStatuses = sortBy(
      process?.processStatuses,
      ({ order }) => -parseInt(order, 10),
    );
    const currentStatusOrder = get(currentStatus, "order");

    let targetStatus;
    let stepsRemaining = steps;
    for (let status of sortedStatuses) {
      if (parseInt(status.order, 10) < currentStatusOrder) {
        stepsRemaining--;
        if (stepsRemaining === 0) {
          targetStatus = status;
          break;
        }
      }
    }
    if (targetStatus) {
      persistStaticResumeStatus(get(runtimeProcess, "theme"), targetStatus.id);
      setCurrentStatus(targetStatus);
    }
  };

  const processTheme =
    get(runtimeProcess, "theme") ||
    get(displayProject, "fields.processTemplate") ||
    "DEFAULT";
  const isDynamicTheme = isDynamicProcessTheme(processTheme);

  useEffect(() => {
    persistStaticResumeStatus(processTheme, get(currentStatus, "id"));
  }, [currentStatus, processTheme]);

  const WithContactCard = useMemo(() => {
    const tracerType = get(runtimeTracer, "type");
    const Template = isDynamicTheme
      ? Templates.dynamic.Template
      : get(
          get(
            Templates[tracerType],
            processTheme,
            Templates[tracerType]?.DEFAULT,
          ),
          get(currentStatus, "template"),
        );
    document.title =
      THEMES_PALETTE[processTheme]?.TITLE || THEMES_PALETTE.DEFAULT.TITLE;
    if (!Template) return;
    return HighContactCard(Template);
  }, [currentStatus?.template, isDynamicTheme, processTheme, runtimeTracer]);

  if (!displayProject || !WithContactCard) {
    const ThemeLoading = get(
      get(Templates[get(runtimeTracer, "type")], processTheme),
      "Loading",
    );
    if (typeof ThemeLoading === "function") {
      return <ThemeLoading />;
    }
    return ThemeLoading || <Loading />;
  }

  return (
    <div
      className={classNames(processTheme, {
        "is-dynamic-run": isDynamicTheme,
      })}
    >
      <div
        className={classNames({
          locked:
            get(runtimeTracer, "type") === TRACER_TYPES.MANUAL &&
            displayProject?.locked,
        })}
      >
        <WithContactCard
          contactId={get(displayProject, "contact.id")}
          visible={
            get(displayProject, "contact.user.firstname") &&
            get(runtimeTracer, "type") === TRACER_TYPES.MANUAL &&
            processTheme !== TEMPLATE_TYPES.B2C_ACORIS
          }
          project={displayProject}
          author={author}
          processId={get(runtimeProcess, "id")}
          tracer={runtimeTracer}
          onNext={onNext}
          onBack={onBack}
          setCurrentStatus={setCurrentStatus}
          setDisplayProject={setDisplayProject}
          updateUrl={updateUrl}
          currentStatus={currentStatus}
          tracers={tracers}
          setTracers={setTracers}
        />
      </div>
    </div>
  );
};

export default Detail;
